import type { ReactNode } from 'react';
import type { StatusKind } from '../../lib/contract';

// ── Badge ─────────────────────────────────────────────────────────────────────
type BadgeKind = 'kev' | 'crit' | 'high' | 'med' | 'low' | 'safe' | 'indet' | 'out' | 'teal' | 'warn';
export function Badge({ kind = 'out', children, style, ...data }:
  { kind?: BadgeKind; children: ReactNode; style?: React.CSSProperties }
  // data-* passthrough (e2e testids) — nothing else leaks onto the element.
  & { [attr: `data-${string}`]: string | undefined }) {
  return <span className={`badge b-${kind}`} style={style} {...data}>{children}</span>;
}

const SEV_BADGE: Record<StatusKind, { kind: BadgeKind; text: string }> = {
  critical: { kind: 'crit', text: '[!] CRIT' }, high: { kind: 'high', text: '[▲] HIGH' },
  medium: { kind: 'med', text: '[●] MED' }, low: { kind: 'low', text: '[-] LOW' },
  indeterminate: { kind: 'indet', text: '[?] INDET' }, safe: { kind: 'safe', text: '[✓] SAFE' },
  uncertain: { kind: 'out', text: 'reachable?' }, dismissed: { kind: 'out', text: 'dismissed (VEX)' },
};
export function SeverityBadge({ kind }: { kind: StatusKind }) {
  const b = SEV_BADGE[kind];
  return <Badge kind={b.kind}>{b.text}</Badge>;
}

// ── Card ──────────────────────────────────────────────────────────────────────
export function Card({ children, style, className, ...data }:
  { children: ReactNode; style?: React.CSSProperties; className?: string }
  // data-* passthrough (e2e testids) — nothing else leaks onto the element.
  & { [attr: `data-${string}`]: string | undefined }) {
  return <div className={`card${className ? ' ' + className : ''}`} style={style} {...data}>{children}</div>;
}

// ── Button ──────────────────────────────────────────────────────────────────────
type BtnVariant = 'primary' | 'ghost' | 'danger';
export function Btn({ variant = 'ghost', sm, children, onClick, disabled, type = 'button', id, ...data }:
  { variant?: BtnVariant; sm?: boolean; children: ReactNode; onClick?: () => void; disabled?: boolean; type?: 'button' | 'submit'; id?: string }
  // data-* passthrough (e2e testids) — nothing else leaks onto the element.
  & { [attr: `data-${string}`]: string | undefined }) {
  return (
    <button id={id} type={type} className={`btn btn-${variant}${sm ? ' sm' : ''}`} onClick={onClick} disabled={disabled} {...data}>
      {children}
    </button>
  );
}

// ── Page / section heads ────────────────────────────────────────────────────────
export function PageHead({ title, sub, crumbs, action }:
  { title: ReactNode; sub?: ReactNode; crumbs?: ReactNode; action?: ReactNode }) {
  return (
    <div>
      {crumbs && <div className="crumbs">{crumbs}</div>}
      <div className="pagehead">
        <div><h1>{title}</h1>{sub && <div className="sub">{sub}</div>}</div>
        {action}
      </div>
    </div>
  );
}

export function SectionHead({ title, aside }: { title: ReactNode; aside?: ReactNode }) {
  return <div className="section-head"><h3>{title}</h3>{aside && <span className="aside">{aside}</span>}</div>;
}

// ── Tabs (controlled) ────────────────────────────────────────────────────────────
export function Tabs<T extends string>({ tabs, value, onChange }:
  { tabs: Array<{ key: T; label: ReactNode }>; value: T; onChange: (k: T) => void }) {
  return (
    <div className="tabs">
      {tabs.map((t) => (
        <div key={t.key} className={`tab${t.key === value ? ' on' : ''}`} onClick={() => onChange(t.key)}>{t.label}</div>
      ))}
    </div>
  );
}

// ── Field ────────────────────────────────────────────────────────────────────────
export function Field({ label, hint, req, children }: { label: ReactNode; hint?: ReactNode; req?: boolean; children: ReactNode }) {
  return (
    <div className="field">
      <label>{label} {req && <span className="req">*</span>} {hint && <span className="hint">{hint}</span>}</label>
      {children}
    </div>
  );
}
